<template>
  <div class="dropdown user-dropdown">
    
    <button 
      class="btn btn-sm btn-outline-primary border-white text-white dropdown-toggle d-flex align-items-center" 
      type="button" 
      id="userDropdownMenu"
      @click="toggleDropdown"
    >
      <i class="fas fa-user-circle me-2"></i>
      <span class="d-none d-md-inline">{{ userName || 'User' }}</span>
    </button>

    <!-- Mobile backdrop -->
    <div 
      v-if="isOpen" 
      class="offcanvas-backdrop d-md-none" 
      @click="closeDropdown"
    ></div>

    <ul 
      class="dropdown-menu dropdown-menu-end" 
      :class="{ 'show': isOpen }"
      @click.stop
    >
      <li><h6 class="dropdown-header">{{ userName }}</h6></li>
      <li v-if="userEmail" class="px-3 text-muted small">{{ userEmail }}</li>
      
      <!-- Clock In/Out - mobile only -->
      <li class="d-md-none">
        <hr class="dropdown-divider">
      </li>
      <li class="d-md-none">
        <button class="dropdown-item" @click="openClockModal">
          <i class="fas fa-clock me-2"></i>
          {{ clockStatusText }}
        </button>
      </li>
      <li v-if="userRoleNames.length > 0" class="px-3 pt-2">
        <div class="d-flex flex-wrap gap-1">
          <span 
            v-for="(role, index) in userRoleNames" 
            :key="index" 
            class="badge bg-secondary-subtle text-secondary-emphasis"
          >
            {{ role }}
          </span>
        </div>
      </li>
      <li><hr class="dropdown-divider"></li>
      
      <!-- System Admin Link -->
      <li v-if="isSystemAdmin">
        <router-link class="dropdown-item fw-bold text-primary" to="/admin/migrations" @click="closeDropdown">
          <i class="fas fa-shield-halved me-2"></i>
          System Admin
        </router-link>
      </li>
      
      <!-- Impersonation Status -->
      <li v-if="isImpersonating">
        <button class="dropdown-item text-warning" @click="exitImpersonation">
          <i class="fas fa-user-secret me-2"></i>
          Exit Impersonation
        </button>
      </li>
      
      <li v-if="isSystemAdmin || isImpersonating"><hr class="dropdown-divider"></li>
      
      <!-- User Profile/Settings -->
      <li>
        <router-link class="dropdown-item" :to="`/tech/edit/${userId}`" @click="closeDropdown">
          <i class="fas fa-user-edit me-2"></i>
          Edit Profile
        </router-link>
      </li>
      
      <li><hr class="dropdown-divider"></li>
      <li><h6 class="dropdown-header text-muted small">My Items</h6></li>
      
      <!-- My Requests -->
      <li>
        <router-link 
          class="dropdown-item" 
          :to="`/job/list?technician=${userId}`" 
          @click="closeDropdown"
        >
          <i class="fas fa-list me-2"></i>
          My Requests
        </router-link>
      </li>
      
      <!-- My Assignments -->
      <li>
        <router-link 
          class="dropdown-item" 
          :to="`/assignments/list?technician=${userId}`" 
          @click="closeDropdown"
        >
          <i class="fas fa-clipboard-check me-2"></i>
          My Assignments
        </router-link>
      </li>
      
      <!-- My Work -->
      <li>
        <router-link 
          class="dropdown-item" 
          :to="`/work/list?technician=${userId}`" 
          @click="closeDropdown"
        >
          <i class="fas fa-clock me-2"></i>
          My Work
        </router-link>
      </li>
      
      <!-- My Parts -->
      <li>
        <router-link 
          class="dropdown-item" 
          :to="`/part/list?technician=${userId}`" 
          @click="closeDropdown"
        >
          <i class="fas fa-puzzle-piece me-2"></i>
          My Parts
        </router-link>
      </li>
      
      <!-- My Expenses -->
      <li>
        <router-link 
          class="dropdown-item" 
          :to="`/expense/list?technician=${userId}`" 
          @click="closeDropdown"
        >
          <i class="fas fa-hand-holding-usd me-2"></i>
          My Expenses
        </router-link>
      </li>
      
      <!-- My Mileage -->
      <li>
        <router-link 
          class="dropdown-item" 
          :to="`/mileage/list?technician=${userId}`" 
          @click="closeDropdown"
        >
          <i class="fas fa-car me-2"></i>
          My Mileage
        </router-link>
      </li>
      
      <!-- My Time Tracker -->
      <li>
        <router-link 
          class="dropdown-item" 
          to="/clock/list" 
          @click="closeDropdown"
        >
          <i class="fas fa-clock me-2"></i>
          My Time Tracker
        </router-link>
      </li>
      
      <li><hr class="dropdown-divider"></li>
      
      <!-- Notifications -->
      <li>
        <router-link 
          class="dropdown-item d-flex align-items-center" 
          to="/notification/list" 
          @click="closeDropdown"
        >
          <i class="fas fa-bell me-2"></i>
          Notifications
          <span v-if="unreadCount > 0" class="badge bg-danger ms-auto">{{ unreadCount }}</span>
        </router-link>
      </li>
      
      <li><hr class="dropdown-divider"></li>
      
      <!-- Logout -->
      <li>
        <router-link class="dropdown-item text-danger" to="/logout" @click="closeDropdown">
          <i class="fas fa-right-from-bracket me-2"></i>
          Logout
        </router-link>
      </li>
    </ul>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useAppStore } from '@/stores/AppStore';
import { useRouter } from 'vue-router';
import TokenHelper from '@/services/TokenHelper';
import Api from '@/services/Api';
import ApiService from '@/services/ApiService';
import Emitter from '@/services/Emitter';
import { useTimeClock } from '@/composables/useTimeClock';

const emit = defineEmits(['open-clock-modal']);

const appStore = useAppStore();
const router = useRouter();
const isOpen = ref(false);
const isSystemAdminValue = ref(false);

// Clock status for mobile menu item
const { isClockedIn, statusText: clockStatusText } = useTimeClock();
const isImpersonatingValue = ref(false);
const impersonationLogId = ref<string | null>(null);
const unreadCount = ref(0);

const loadUnreadCount = async () => {
  try {
    unreadCount.value = await ApiService.getUnreadNotificationCount(true);
  } catch (error) {
    console.error('Error loading unread count:', error);
  }
};

// Get user info from store
// Use flat properties that work with both login and KeepAlive responses
// Fall back to nested User object for backwards compatibility with login response
const userName = computed(() => 
  appStore.account.user?.Name || 
  (appStore.account.user as any)?.User?.TechnicianName || 
  'User'
);
const userEmail = computed(() => 
  appStore.account.user?.Email || 
  (appStore.account.user as any)?.User?.Email || 
  ''
);
const userId = computed(() => 
  appStore.account.user?.UserId || 
  (appStore.account.user as any)?.User?.Id || 
  ''
);

// Get user roles from store
const userRoleNames = computed(() => {
  const roles = appStore.account.user?.Roles;
  if (!roles) return [];
  return Object.values(roles).map(role => role.Name).filter(Boolean);
});

// Get admin status from token
const isSystemAdmin = computed(() => isSystemAdminValue.value);
const isImpersonating = computed(() => isImpersonatingValue.value);

const toggleDropdown = () => {
  isOpen.value = !isOpen.value;
};

const closeDropdown = () => {
  isOpen.value = false;
};

const openClockModal = () => {
  closeDropdown();
  emit('open-clock-modal');
};

// Close dropdown when clicking outside
const handleClickOutside = (event: MouseEvent) => {
  const dropdown = document.querySelector('.user-dropdown');
  if (dropdown && !dropdown.contains(event.target as Node)) {
    closeDropdown();
  }
};

onMounted(async () => {
  document.addEventListener('click', handleClickOutside);
  
  // Load admin and impersonation status from token
  isSystemAdminValue.value = await TokenHelper.isSystemAdmin();
  isImpersonatingValue.value = await TokenHelper.isImpersonating();
  impersonationLogId.value = await TokenHelper.getImpersonationLogId();
  
  // Load unread notification count
  loadUnreadCount();
});

Emitter.on("notification:refresh", loadUnreadCount);

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
});

const exitImpersonation = async () => {
  try {
    if (!impersonationLogId.value) {
      console.error('No impersonation log ID found');
      return;
    }

    // Call API to end impersonation
    const response = await Api.post('/api/admin/impersonation/end', {
      impersonationLogId: impersonationLogId.value
    });

    if (response.data.success) {
      // Clear impersonation state
      isImpersonatingValue.value = false;
      impersonationLogId.value = null;
      
      // Reload the page to get original token context
      alert('Impersonation ended. Reloading...');
      window.location.reload();
    }
  } catch (error) {
    console.error('Error ending impersonation:', error);
    alert('Failed to end impersonation. Please try again.');
  }
  closeDropdown();
};
</script>

<style scoped>
.user-dropdown {
  position: relative;
}

.user-dropdown .dropdown-menu {
  min-width: 250px;
  margin-top: 0.5rem;
  right: 0;
  left: auto;
}

.user-dropdown .dropdown-item {
  padding: 0.5rem 1rem;
  transition: all 0.2s;
}

.user-dropdown .dropdown-item:hover {
  background-color: #f8f9fa;
  padding-left: 1.5rem;
}

.user-dropdown .dropdown-item i {
  width: 20px;
  text-align: center;
}

.user-dropdown .dropdown-header {
  font-weight: 600;
  color: #495057;
}

/* Mobile offcanvas behavior */
.offcanvas-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1040;
}

@media (max-width: 767.98px) {
  .user-dropdown .dropdown-menu {
    position: fixed !important;
    top: 0 !important;
    right: 0 !important;
    bottom: 0 !important;
    left: auto !important;
    width: 280px;
    max-width: 80vw;
    height: 100vh;
    margin: 0 !important;
    border-radius: 0;
    border: none;
    box-shadow: -4px 0 16px rgba(0, 0, 0, 0.2);
    overflow-y: auto;
    z-index: 1050;
    padding-top: 1rem;
    padding-bottom: 2rem;
    transform: translateX(100%);
    transition: transform 0.3s ease-in-out;
    display: block !important;
  }

  .user-dropdown .dropdown-menu.show {
    transform: translateX(0);
  }
}
</style>
